import type {ElementType, ReactNode} from "react";
import {Mail, Phone, User} from "lucide-react";

import {Button} from "@/components/ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {TruncatedWithTooltip} from "@/src/modules/core/components";
import {formatUsPhoneDisplay, normalizeUsPhoneDigits} from "@/src/modules/properties/lib/us-phone";
import {ICONS} from "@/src/modules/routing/utils/constants";

import {cn} from "@/lib/utils";

import {DISPATCH_DESK_PANEL_SCROLL_CLASS, DISPATCH_DESK_PANEL_SCROLL_DESKTOP_ONLY_CLASS} from "../lib/dispatch-desk-layout";



/** Dense card shell for dispatch desk property panels. */
export const DISPATCH_DESK_CARD_CLASS = "gap-0 py-4";

/** Shared block header — matches property panels (Emergency contacts, Managers, etc.). */
export const DISPATCH_DESK_BLOCK_HEADER_CLASS = "shrink-0 grid-rows-1 gap-0 pb-2";

/** @deprecated Prefer grid CardHeader + CardAction — flex + items-center drops the title vs property panels. */
export const DISPATCH_DESK_BLOCK_HEADER_ACTIONS_CLASS =
  "flex flex-row items-start justify-between gap-2";

/** Compact header controls — match title row height (~text-sm), not default sm buttons. */
export const DISPATCH_DESK_HEADER_ICON_ACTION_CLASS = "size-7 shrink-0";

export const DISPATCH_DESK_HEADER_TEXT_ACTION_CLASS = "h-7 shrink-0 gap-1 px-2 text-xs";

export const DISPATCH_DESK_BLOCK_TITLE_CLASS = "text-sm leading-none";

/** Body copy in desk cards — matches Property rules row labels (`Citation at-risk margin`). */
export const DISPATCH_DESK_CONTENT_TEXT_CLASS = "text-sm leading-snug";

/** Muted secondary copy — phones, field values, section headers (Emergency contacts reference). */
export const DISPATCH_DESK_CONTENT_MUTED_CLASS = "text-muted-foreground text-sm leading-snug";

/** Row primary (left): names, field labels, rule names — `text-foreground` via card body. */
export const DISPATCH_DESK_ROW_PRIMARY_CLASS = DISPATCH_DESK_CONTENT_TEXT_CLASS;

/** Row secondary (right): phones, values, dates — muted like emergency contact numbers. */
export const DISPATCH_DESK_ROW_SECONDARY_CLASS = DISPATCH_DESK_CONTENT_MUTED_CLASS;

/** General information value icons — aligned with sidebar (`properties`, `households`) + single resident. */
export const DISPATCH_DESK_GENERAL_INFO_ICONS = {
  property: ICONS.properties,
  household: ICONS.households,
  resident: User,
} as const satisfies Record<string, ElementType>;

/** Fixed viewport panel shell — fills flex slice on desktop; grows with content on mobile. */
export const DISPATCH_DESK_PANEL_SHELL_CLASS =
  "flex flex-col lg:h-full lg:min-h-0 lg:overflow-hidden";

/** Card body — grow on mobile unless `contentClassName` overrides with capped layout. */
export const DISPATCH_DESK_CARD_CONTENT_CLASS =
  "flex flex-col pt-0 lg:min-h-0 lg:flex-1 lg:overflow-hidden";



/** Muted empty-state line inside a desk section. */

export const DISPATCH_DESK_EMPTY_MESSAGE_CLASS = DISPATCH_DESK_CONTENT_MUTED_CLASS;

/** Indent nested content under a section header (rules, contacts, notes). */
export const DISPATCH_DESK_NESTED_INDENT_CLASS = "pl-[1.125rem]";

/** Separates card body from list/detail sections — General information, Managers, etc. */
export const DISPATCH_DESK_SECTION_CLASS = "border-border flex flex-col gap-1.5 border-t pt-3";



type DispatchDeskCardShellProps = {

  className?: string;

  icon: ElementType;

  iconClassName?: string;

  title: string;

  titleSuffix?: ReactNode;

  children: ReactNode;

  contentClassName?: string;

};



type DispatchDeskHeaderIconRowProps = {

  icon: ElementType;

  iconClassName?: string;

  children: ReactNode;

  className?: string;

};



/** Icon + title row for right-column desk cards (Quick actions, On duty, etc.). */

export function DispatchDeskHeaderIconRow({

  icon: Icon,

  iconClassName,

  children,

  className,

}: DispatchDeskHeaderIconRowProps) {

  return (

    <div className={cn("flex min-w-0 items-center gap-1.5", className)}>

      <Icon className={cn("size-3.5 shrink-0", iconClassName ?? "text-primary")} aria-hidden />

      {children}

    </div>

  );

}



export function DispatchDeskCardShell({

  className,

  icon: Icon,

  iconClassName,

  title,

  titleSuffix,

  children,

  contentClassName,

}: DispatchDeskCardShellProps) {

  return (

    <Card className={cn(DISPATCH_DESK_CARD_CLASS, DISPATCH_DESK_PANEL_SHELL_CLASS, className)}>

      <CardHeader className={DISPATCH_DESK_BLOCK_HEADER_CLASS}>

        <div className="flex min-w-0 items-center gap-1.5">

          <Icon className={cn("size-3.5 shrink-0", iconClassName)} />

          <CardTitle className={DISPATCH_DESK_BLOCK_TITLE_CLASS}>{title}</CardTitle>

          {titleSuffix}

        </div>

      </CardHeader>

      <CardContent
        className={cn(DISPATCH_DESK_CARD_CONTENT_CLASS, contentClassName)}
      >

        {children}

      </CardContent>

    </Card>

  );

}



export function DispatchDeskEmptyMessage({children}: {children: ReactNode}) {

  return <p className={DISPATCH_DESK_EMPTY_MESSAGE_CLASS}>{children}</p>;

}



/** Centers empty copy inside a flex panel body (preserves full card height). */

export function DispatchDeskEmptyPanelBody({children}: {children: ReactNode}) {

  return (

    <div className="flex flex-col items-center justify-center text-center lg:min-h-0 lg:flex-1">

      {children}

    </div>

  );

}

type DispatchDeskNestedListProps = {
  children: ReactNode;
  className?: string;
};

/** Indented stack under a card section — emergency contacts, managers, rule details. */
export function DispatchDeskNestedList({children, className}: DispatchDeskNestedListProps) {
  return (
    <div className={cn("flex flex-col gap-1.5", DISPATCH_DESK_NESTED_INDENT_CLASS, className)}>
      {children}
    </div>
  );
}

type DispatchDeskSectionProps = {
  children: ReactNode;
  className?: string;
};

/** Top border + spacing before nested body content. */
export function DispatchDeskSection({children, className}: DispatchDeskSectionProps) {
  return <div className={cn(DISPATCH_DESK_SECTION_CLASS, className)}>{children}</div>;
}

type DispatchDeskLabelRowProps = {
  label: string;
  children: ReactNode;
  labelClassName?: string;
  /** Use `start` when the value may wrap to multiple lines. */
  align?: "center" | "start";
};

/** One line: primary label left, secondary value right — General info / Telephone pattern. */
export function DispatchDeskLabelRow({
  label,
  children,
  labelClassName,
  align = "center",
}: DispatchDeskLabelRowProps) {
  return (
    <div className={cn("flex gap-2", align === "start" ? "items-start" : "items-center")}>
      <span
        className={cn(
          DISPATCH_DESK_ROW_PRIMARY_CLASS,
          "w-[5.25rem] shrink-0",
          labelClassName,
        )}
      >
        {label}
      </span>
      <div
        className={cn(
          "flex min-w-0 flex-1 justify-end",
          DISPATCH_DESK_ROW_SECONDARY_CLASS,
        )}
      >
        {children}
      </div>
    </div>
  );
}

type DispatchDeskLabeledFieldProps = {
  label: string;
  children: ReactNode;
  className?: string;
};

/** Label on top, indented body below — multi-line Notes pattern. */
export function DispatchDeskLabeledField({label, children, className}: DispatchDeskLabeledFieldProps) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <span className={cn(DISPATCH_DESK_ROW_PRIMARY_CLASS, "font-normal")}>{label}</span>
      <div className={cn(DISPATCH_DESK_NESTED_INDENT_CLASS, DISPATCH_DESK_ROW_SECONDARY_CLASS)}>{children}</div>
    </div>
  );
}

type DispatchDeskContactRowProps = {
  name: string;
  phone?: string | null;
  callLabel?: string;
};

/** Indented contact line: primary name left, muted phone right — emergency contacts pattern. */
export function DispatchDeskContactRow({name, phone, callLabel}: DispatchDeskContactRowProps) {
  return (
    <div className="flex items-start justify-between gap-2">
      <span className={cn(DISPATCH_DESK_ROW_PRIMARY_CLASS, "min-w-0 flex-1 break-words")}>
        {name}
      </span>
      <DispatchDeskPhoneRow value={phone} callLabel={callLabel ?? name} size="sm" />
    </div>
  );
}



type DispatchDeskScrollRegionProps = {

  children: ReactNode;

  className?: string;

  /** When true, scroll on mobile too (parent panel must use a mobile `max-h` cap). */
  capped?: boolean;

};



/** Bounded scroll area inside a desk card (emergency list, rules accordion). */

export function DispatchDeskScrollRegion({
  children,
  className,
  capped = false,
}: DispatchDeskScrollRegionProps) {

  return (

    <div
      className={cn(
        capped ? DISPATCH_DESK_PANEL_SCROLL_CLASS : DISPATCH_DESK_PANEL_SCROLL_DESKTOP_ONLY_CLASS,
        "pr-0.5",
        className,
      )}
    >

      {children}

    </div>

  );

}



type DispatchDeskMetaListProps = {

  children: ReactNode;

  className?: string;

};



/** Vertical stack for property metadata — no inner border (flat inside one card). */

export function DispatchDeskMetaList({children, className}: DispatchDeskMetaListProps) {

  return <div className={cn("flex flex-col gap-2", className)}>{children}</div>;

}



type DispatchDeskMetaRowProps = {

  label: string;

  children: ReactNode;

};



/** Compact inline label/value — primary label left, secondary value right. */

export function DispatchDeskMetaRow({label, children}: DispatchDeskMetaRowProps) {

  return (

    <div className="flex items-start gap-2">

      <span className={cn(DISPATCH_DESK_ROW_PRIMARY_CLASS, "w-[5.25rem] shrink-0")}>{label}</span>

      <div className={cn("min-w-0 flex-1", DISPATCH_DESK_ROW_SECONDARY_CLASS)}>{children}</div>

    </div>

  );

}



type DispatchDeskMetaNotesProps = {

  label: string;

  children: ReactNode;

};



/** Multi-line field at the bottom of a meta list (notes, long copy). */

export function DispatchDeskMetaNotes({label, children}: DispatchDeskMetaNotesProps) {

  return (

    <div className="border-border flex flex-col gap-0.5 border-t pt-2">

      <span className={DISPATCH_DESK_ROW_PRIMARY_CLASS}>{label}</span>

      <div className={DISPATCH_DESK_ROW_SECONDARY_CLASS}>{children}</div>

    </div>

  );

}



type DispatchDeskPhoneRowProps = {

  value?: string | null;

  callLabel: string;

  /** Match parent value size — `sm` for meta rows, `xs` for entity rows. */

  size?: "xs" | "sm";

};



type DispatchDeskIconValueRowProps = {
  value: string;
  icon: ElementType;
  iconLabel: string;
  /** Show full value with line breaks instead of truncating (property / household lines). */
  wrap?: boolean;
};

const DISPATCH_DESK_VALUE_ICON_CLASS =
  "text-muted-foreground inline-flex size-6 shrink-0 items-center justify-center";

/** Secondary value + trailing icon — mirrors `DispatchDeskPhoneRow` layout for general info fields. */
export function DispatchDeskIconValueRow({
  value,
  icon: Icon,
  iconLabel,
  wrap = false,
}: DispatchDeskIconValueRowProps) {
  return (
    <div
      className={cn(
        "flex min-w-0 max-w-full justify-end gap-1",
        wrap ? "items-start" : "items-center",
      )}
    >
      {wrap ? (
        <span
          className={cn(
            "min-w-0 flex-1 text-right break-words",
            DISPATCH_DESK_ROW_SECONDARY_CLASS,
          )}
        >
          {value}
        </span>
      ) : (
        <TruncatedWithTooltip
          content={value}
          className={cn("block min-w-0 flex-1 truncate text-right", DISPATCH_DESK_ROW_SECONDARY_CLASS)}
        >
          {value}
        </TruncatedWithTooltip>
      )}
      <span className={DISPATCH_DESK_VALUE_ICON_CLASS} title={iconLabel} aria-hidden>
        <Icon className="size-3" />
      </span>
    </div>
  );
}



export function DispatchDeskPhoneRow({value, callLabel, size = "sm"}: DispatchDeskPhoneRowProps) {

  const display = formatUsPhoneDisplay(value);

  const digits = value ? normalizeUsPhoneDigits(value) : "";



  if (!digits || display === "-") {

    return <span className="tabular-nums">-</span>;

  }



  const linkClass =
    size === "sm"
      ? cn(DISPATCH_DESK_ROW_SECONDARY_CLASS, "tabular-nums")
      : "text-muted-foreground text-xs tabular-nums leading-snug";



  return (

    <div className="flex shrink-0 items-center gap-1">

      <a

        href={`tel:${digits}`}

        className={cn("hover:text-foreground", linkClass)}

      >

        {display}

      </a>

      <Button

        variant="ghost"

        size="icon"

        className="size-6 shrink-0"

        aria-label={`Call ${callLabel}`}

        render={<a href={`tel:${digits}`} />}

      >

        <Phone className="size-3" />

      </Button>

    </div>

  );

}



type DispatchDeskPersonRowProps = {

  name: string;

  email?: string | null;

  phone?: string | null;

  className?: string;

};



/** Flat person row — managers; divider applied by parent between rows. */

export function DispatchDeskPersonRow({name, email, phone, className}: DispatchDeskPersonRowProps) {

  const phoneDigits = phone ? normalizeUsPhoneDigits(phone) : "";

  const phoneDisplay = formatUsPhoneDisplay(phone);

  const hasPhone = Boolean(phoneDigits && phoneDisplay !== "-");



  return (

    <div className={cn("flex flex-col gap-0.5 py-2 first:pt-0 last:pb-0", className)}>

      <p className={cn("font-normal", DISPATCH_DESK_CONTENT_TEXT_CLASS)}>{name}</p>

      {email ? (

        <a

          href={`mailto:${email}`}

          className={cn("text-muted-foreground hover:text-foreground flex items-center gap-1", DISPATCH_DESK_CONTENT_TEXT_CLASS)}

        >

          <Mail className="size-3 shrink-0" />

          <span className="truncate">{email}</span>

        </a>

      ) : null}

      {hasPhone ? <DispatchDeskPhoneRow value={phone} callLabel={name} /> : null}

    </div>

  );

}

